import { Box } from "@mui/material";
import { colors } from "../../theme/palette";
import { useState } from "react";
import SearchFilter from "./SearchFilter";
import { useQuery } from "@tanstack/react-query";
import { entityGenderEnum } from "../../enums/entity.enum";
import {
  getTrendingEntities,
  getTrendingProfessionals,
  getTrendingTalents,
  searchEntities,
  searchUsers,
} from "../../api/network";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Trending from "../../shared/Trending";
import {
  TrendingEntitiesIcon,
  TrendingProfessionalsIcon,
  TrendingTalentsIcon,
} from "../../assets/icons/MUI/NetworkIcons";
import ItemsList from "./ItemsList";
import UsersEntitiesSearch from "./UsersEntitiesSearch";

const Search = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const type = searchParams.get("type");
  const value = searchParams.get("value");

  const [page, setPage] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [gender, setGender] = useState<{
    label: string;
    value: entityGenderEnum;
  } | null>(null);
  const [nationality, setNationality] = useState<{
    label: string;
    value: string;
  } | null>(null);
  const [country, setCountry] = useState<{
    label: string;
    value: string;
  } | null>(null);
  const [persona, setPersona] = useState<{
    label: string;
    value: string;
  } | null>(null);
  const [isVerified, setIsVerified] = useState<{
    label: string;
    value: string;
  } | null>(null);

  const fetchSearchList = () => {
    if (type === "entities")
      return searchEntities(
        8,
        page,
        value || "",
        country?.value,
        nationality?.value,
        false,
        false,
        isVerified?.value === "VERIFIED" ? "VERIFIED" : isVerified?.value === "UNVERIFIED" ? "UNVERIFIED" : ""
      );

    if (type === "users")
      return searchUsers(
        8,
        page,
        value || "",
        country?.value,
        nationality?.value,
        gender?.value || "",
        isVerified?.value === "verified" ? "VERIFIED" : "",
        persona?.value || ""
      );

    return [];
  };

  const { data: searchData, isLoading: isSearchDataLoading } = useQuery({
    queryKey: ["searchData", page, nationality, gender, country, persona, isVerified],
    queryFn: fetchSearchList,
    enabled: !!type,
  });

  const searchingList = searchData?.items || [];
  const paginationData = searchData?.meta || {
    currentPage: 0,
    itemCount: 0,
    itemsPerPage: 0,
    totalItems: 0,
    totalPages: 0,
  };

  const handleChangePage = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value - 1);
  };

  const { data: trendingTalents, isLoading: isTrendingTalentsLoading } = useQuery({
    queryKey: ["trendingTalents"],
    queryFn: async () => await getTrendingTalents(),
  });

  const { data: trendingProfessionals, isLoading: isTrendingProfessionalsLoading } = useQuery({
    queryKey: ["trendingProfessionals"],
    queryFn: async () => await getTrendingProfessionals(),
  });

  const { data: trendingEntities, isLoading: isTrendingEntitiesLoading } = useQuery({
    queryKey: ["trendingEntities"],
    queryFn: async () => await getTrendingEntities(),
  });

  const resetFilters = () => {
    setGender(null);
    setNationality(null);
    setCountry(null);
    setPersona(null);
    setIsVerified(null);
  };

  if (!type && !value) return <>Not found</>;

  return (
    <Box
      sx={{
        display: "flex",
        gap: "15px",
      }}
    >
      <Box
        sx={{
          padding: "12px",
          background: colors.White,
          borderRadius: "8px",
          border: "1px solid #EAECF0",
          width: "870px",
          overflow: "hidden",
          height: "fit-content",
        }}
      >
        {type && (
          <SearchFilter
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            gender={gender}
            setGender={setGender}
            nationality={nationality}
            setNationality={setNationality}
            country={country}
            setCountry={setCountry}
            persona={persona}
            setPersona={setPersona}
            type={type}
            resetFilters={resetFilters}
            isVerified={isVerified}
            setIsVerified={setIsVerified}
          />
        )}

        {type && (
          <ItemsList
            isSearchDataLoading={isSearchDataLoading}
            paginationData={paginationData}
            type={type}
            searchingList={searchingList}
            page={page}
            handleChangePage={handleChangePage}
          />
        )}

        {!type && !!value && <UsersEntitiesSearch value={value} />}
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "14px" }}>
        <Trending
          icon={<TrendingTalentsIcon />}
          label={t("components.trending.talents")}
          users={trendingTalents}
          isLoading={isTrendingTalentsLoading}
          query="talents"
          sum={true}
        />
        <Trending
          icon={<TrendingProfessionalsIcon />}
          label={t("components.trending.professionals")}
          users={trendingProfessionals}
          isLoading={isTrendingProfessionalsLoading}
          query="professionals"
        />
        <Trending
          icon={<TrendingEntitiesIcon />}
          label={t("components.trending.entities")}
          users={trendingEntities}
          isLoading={isTrendingEntitiesLoading}
          query="entities"
          sum={true}
        />
      </Box>
    </Box>
  );
};

export default Search;
